import { Box, IconButton, Link, SvgIcon, TableCell, TableRow, type Theme } from "@mui/material";
import { isNil } from "ramda";
import React from "react";
import { Trash2 as DeleteIcon, Edit as EditIcon } from "react-feather";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { useOnOffSwitch } from "../helpers/hooks";
import { Spacing } from "../types/enum";
import { type IllustrationModel } from "../types/graphql";

import ConfirmationModal from "./ConfirmationModal";
import Label from "./Label";

/**
 * Types
 */
interface Props {
  illustration: IllustrationModel;
  onDelete: (illustrationId: string) => void;
}

/**
 * Styles
 */
const useStyles = makeStyles()((theme: Theme) => ({
  image: {
    width: "auto",
    maxHeight: "150px",
    height: "auto",
    display: "block",
    borderRadius: theme.shape.borderRadius,
  },
  placeholder: {
    height: "100%",
    padding: theme.spacing(Spacing.ml),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.action.disabled}`,
  },
  item: {
    maxWidth: "250px",
  },
}));

const IllustrationsPageIllustrationDetails: React.FC<Props> = ({
  illustration,
  onDelete,
}: Props) => {
  const { classes } = useStyles();

  const [open, onOpen, onClose] = useOnOffSwitch();

  const renderers = {
    image: (variant: string | undefined | null) => {
      if (isNil(variant)) {
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={classes.placeholder}
          >
            <Label variant="error">Missing image</Label>
          </Box>
        );
      }
      return <img src={variant} alt={variant} className={classes.image} />;
    },
  };

  return (
    <TableRow>
      <TableCell>
        {illustration?.title && <Label variant="info">{illustration?.title}</Label>}
      </TableCell>

      <TableCell>{renderers.image(illustration?.url)}</TableCell>

      <TableCell align="right">
        <Link component={RouterLink} to={`/illustrations/${illustration.id}/edit`}>
          <IconButton size="large">
            <SvgIcon fontSize="small">
              <EditIcon />
            </SvgIcon>
          </IconButton>
        </Link>
        <IconButton onClick={onOpen} size="large">
          <SvgIcon fontSize="small">
            <DeleteIcon />
          </SvgIcon>
        </IconButton>
      </TableCell>

      <ConfirmationModal
        open={open}
        onSubmit={() => {
          onDelete(illustration?.id);
        }}
        onClose={onClose}
      />
    </TableRow>
  );
};

export default IllustrationsPageIllustrationDetails;
